import {
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useInsightStore } from "../../stores/useInsightStore";
import { useLogicChain } from "../../hooks/useLogicChain";
import { useEffect } from "react";
import BrainRegionBadge from "./BrainRegionBadge";
import LogicChainTimeline from "./LogicChainTimeline";
import RetrievedChunks from "./RetrievedChunks";
import TokenUsageChart from "./TokenUsageChart";

interface InsightPanelProps {
  onClose?: () => void;
}

export default function InsightPanel({ onClose }: InsightPanelProps) {
  const { activeMessageId, currentChain, setLoading } = useInsightStore();
  const { chain, loading, fetchChainByMessage } = useLogicChain();

  useEffect(() => {
    if (activeMessageId) {
      setLoading(true);
      fetchChainByMessage(activeMessageId).then((data) => {
        if (data) {
          useInsightStore.getState().loadChain(data);
        }
        setLoading(false);
      });
    }
  }, [activeMessageId, fetchChainByMessage, setLoading]);

  const displayChain = currentChain || chain;

  return (
    <Box sx={{ width: 320, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#7C6F5C", fontSize: "1rem" }}>
          逻辑洞察
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: "#A99F8E" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "#E8E2D9" }} />

      <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: "#7C6F5C" }} />
          </Box>
        ) : !displayChain ? (
          <Typography variant="body2" sx={{ color: "#A99F8E", textAlign: "center", py: 4 }}>
            点击消息旁的 🔍 查看生成逻辑
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <BrainRegionBadge
              region={displayChain.detected_brain_region}
              confidence={
                displayChain.confidence ||
                displayChain.config_snapshot?.strategy?.brain_region_confidence
              }
            />
            <LogicChainTimeline stage={displayChain.coaching_stage} />
            {displayChain.config_snapshot?.strategy && (
              <Box>
                <Typography variant="caption" sx={{ color: "#A99F8E", fontWeight: 600 }}>
                  生成策略
                </Typography>
                <Box sx={{ mt: 0.75, p: 1.25, bgcolor: "#F7F4EF", borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: "#7C6F5C", display: "block", lineHeight: 1.7 }}>
                    对话分支: {displayChain.config_snapshot.strategy.target}
                    <br />
                    模型侧: {displayChain.config_snapshot.strategy.model_label}
                    <br />
                    回应模式: {displayChain.config_snapshot.strategy.response_mode}
                    <br />
                    RAG检索: {displayChain.config_snapshot.strategy.rag_triggered ? "已触发" : "未触发"}
                    <br />
                    阶段判断: {displayChain.config_snapshot.strategy.coaching_stage}
                  </Typography>
                </Box>
              </Box>
            )}
            <RetrievedChunks chunks={displayChain.retrieved_chunks || []} />
            {displayChain.config_snapshot?.prompt_detail?.rag_context && (
              <Box>
                <Typography variant="caption" sx={{ color: "#A99F8E", fontWeight: 600 }}>
                  注入给模型的资料上下文
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#7C6F5C",
                    display: "block",
                    mt: 0.75,
                    p: 1.25,
                    bgcolor: "#F7F4EF",
                    borderRadius: 1,
                    whiteSpace: "pre-wrap",
                    maxHeight: 180,
                    overflow: "auto",
                  }}
                >
                  {displayChain.config_snapshot.prompt_detail.rag_context}
                </Typography>
              </Box>
            )}
            <TokenUsageChart usage={displayChain.token_usage} />
            {displayChain.config_snapshot && (
              <Box>
                <Typography variant="caption" sx={{ color: "#A99F8E", fontWeight: 600 }}>
                  配置快照
                </Typography>
                <Typography variant="caption" sx={{ color: "#7C6F5C", display: "block", mt: 0.5 }}>
                  Prompt版本: {displayChain.config_snapshot.system_prompt_version}
                  <br />
                  RAG: {displayChain.config_snapshot.rag_enabled ? "开启" : "关闭"}
                  <br />
                  Top-K: {displayChain.config_snapshot.rag_top_k}
                </Typography>
              </Box>
            )}
            {displayChain.generation_time_ms && (
              <Typography variant="caption" sx={{ color: "#A99F8E" }}>
                生成耗时: {(displayChain.generation_time_ms / 1000).toFixed(2)}s
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
